import React, { useEffect, useState } from "react";
import { Button, Select, Spin, message } from "antd";
import { createProjectComponent, getAllProjectComponents, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const ProjectComponentExceptionComponent = ({ exception, onClose, onResolved }) => {
    const [componentName, setComponentName] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showExistingSelect, setShowExistingSelect] = useState(false);
    const [components, setComponents] = useState([]);
    const [selectedComponentId, setSelectedComponentId] = useState(null);

    useEffect(() => {
        setComponentName(exception?.unknown_infomation || null);
        setProjectId(exception?.project_id || null);

        const fetchComponents = async () => {
            if (!exception?.project_id) return;
            setLoading(true);
            try {
                const response = await getAllProjectComponents(exception.project_id);
                const data = response?.data || [];
                setComponents(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Failed to fetch project components", error);
                setComponents([]);
            } finally {
                setLoading(false);
            }
        };

        fetchComponents();
    }, [exception]);

    const handleResolve = async () => {
        await updateException(exception.id);
        try { if (typeof onResolved === 'function') await onResolved(); } catch {}
        onClose();
    };

    const handleMarkAsNew = async () => {
        if (!componentName || !projectId) {
            message.error("Missing component name or project.");
            return;
        }

        if (exception.status !== ExceptionStatus.PENDING) {
            onClose();
            return;
        }

        setLoading(true);
        try {
            await createProjectComponent({
                name: componentName,
                project_id: projectId
            });
            await handleResolve();
            message.success("Project component created.");
        } catch (error) {
            console.error("Failed to create project component", error);
            message.error("Failed to create project component.");
        } finally {
            setLoading(false);
        }
    };

    const handleUseExisting = async () => {
        if (!selectedComponentId) {
            message.warning("Select an existing component first.");
            return;
        }

        if (exception.status !== ExceptionStatus.PENDING) {
            onClose();
            return;
        }

        setLoading(true);
        try {
            // Selecting existing component acknowledges this exception as resolved.
            await handleResolve();
            message.success("Exception resolved using existing component.");
        } catch (error) {
            console.error("Failed to resolve exception", error);
            message.error("Failed to resolve exception.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <Button
                            type="primary"
                            onClick={handleMarkAsNew}
                            disabled={loading || !projectId || !componentName}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button
                            type="default"
                            onClick={() => setShowExistingSelect(true)}
                            disabled={loading}
                        >
                            Use Existing
                        </Button>
                        {showExistingSelect && (
                            <>
                                <div style={{ fontSize: 12, color: "#666" }}>
                                    {componentName
                                        ? `Select an existing component to resolve "${componentName}".`
                                        : "Select an existing component."}
                                </div>
                                <Select
                                    style={{ minWidth: 240 }}
                                    placeholder="Select project component"
                                    value={selectedComponentId ?? undefined}
                                    onChange={(value) => setSelectedComponentId(value ?? null)}
                                    disabled={loading}
                                >
                                    {components.map((component) => (
                                        <Option key={component.id} value={component.id}>
                                            {component.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleUseExisting}
                                    disabled={loading || !selectedComponentId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default ProjectComponentExceptionComponent;
